import { useMemo, useEffect } from 'react'

import AgoraRTC, {
  useLocalScreenTrack,
  useJoin,
  usePublish,
  useTrackEvent,
} from 'agora-rtc-react'

import { useLocalPlayer } from '../../context/localPlayerContext'
import { VideoCallScene } from '../../types'
import { generateChannelName } from '../../utils/generateChannelName'

type Props = {
  videoCallUserUid: number
  roomUid: string
  token: string | null
  scene: VideoCallScene
}

// UIを持たないのでhooksでも良いが、stateに応じて条件付きでレンダリングできるようにコンポーネント化している
// 同じクライアントのインスタンスだと一度Offにした後の画面共有ができない問題があるのでそれを回避するためでもある
export const LocalScreenSharePlayer: React.FC<Props> = ({
  videoCallUserUid,
  roomUid,
  token,
  scene,
}) => {
  const { localScreenEnabled, stopLocalScreen } = useLocalPlayer()

  const screenShareClient = useMemo(
    () =>
      AgoraRTC.createClient({
        mode: 'rtc',
        codec: 'vp8',
      }),
    [],
  )

  const { screenTrack: localScreenTrack, error } = useLocalScreenTrack(
    localScreenEnabled,
    {},
    'auto',
  )

  // 共有画面選択ダイアログでキャンセルした場合に発生するエラー
  // この場合は画面共有を停止する
  useEffect(() => {
    if (error?.rtcMethod !== 'IAgoraRTC.createScreenVideoTrack') {
      return
    }
    if (localScreenEnabled) {
      stopLocalScreen()
    }
  }, [error, localScreenEnabled, stopLocalScreen])

  const screenVideoTrack = Array.isArray(localScreenTrack)
    ? localScreenTrack[0]
    : localScreenTrack
  const screenAudioTrack = Array.isArray(localScreenTrack)
    ? localScreenTrack[1]
    : null

  useJoin(
    {
      appid: import.meta.env.VITE_AGORA_APP_ID,
      channel: generateChannelName(roomUid, scene),
      token,
      uid: videoCallUserUid,
    },
    localScreenEnabled && !!localScreenTrack,
    screenShareClient,
  )

  usePublish(
    [screenVideoTrack, screenAudioTrack],
    localScreenEnabled,
    screenShareClient,
  )

  // ブラウザ操作で画面共有が終了された場合に、画面共有を終了する
  useTrackEvent(screenVideoTrack, 'track-ended', () => {
    if (localScreenEnabled) {
      stopLocalScreen()
    }
  })

  return null
}
